import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Every currently-running order, dine-in and Takeaway/Delivery alike —
// CaptainDashboard's getAllRunningOrders builds each row from the same two
// table shapes ActionPanel already knows how to close/view (a floor-plan
// tile for dine-in, the synthetic stand-in findSelectedTable builds for the
// Takeaway/Delivery dropdown), so this modal just reuses
// onCloseOrder/onViewOrder unchanged instead of a new write path.
// Split into tabs by order type, each with its own "select all -> close
// selected" bulk action alongside the per-row Close button — same
// select/close-selected shape PendingItemsModal already uses for its own bulk
// action, just closing orders instead of sending items to the kitchen.
class OrdersInProgressModal extends Component {

    state = { selectedKeys: [] };

    toggleSelected = (key, checked) => {
        this.setState(prev => ({
            selectedKeys: checked
                ? [...prev.selectedKeys, key]
                : prev.selectedKeys.filter(k => k !== key),
        }));
    };

    toggleSelectAll = (list, checked) => {
        const keys = list.map(o => o.key);
        this.setState(prev => ({
            selectedKeys: checked
                ? Array.from(new Set([...prev.selectedKeys, ...keys]))
                : prev.selectedKeys.filter(k => !keys.includes(k)),
        }));
    };

    // Same "hard to reverse, confirm first" pattern as ActionPanel's
    // confirmCloseOrder. Rendered by react-confirm-alert straight onto
    // document.body, same as PrimeReact's own Dialog mask — the stylesheet
    // override in CaptainDashboard.scss is what keeps this on top of the
    // modal it's confirming inside of, instead of hiding behind it.
    confirmCloseOrder = (order) => {
        const { onCloseOrder } = this.props;
        confirmAlert({
            title: 'Close this order?',
            message: `${order.label} will be closed. This can't be undone from here.`,
            buttons: [
                { label: 'Close Order', onClick: () => onCloseOrder && onCloseOrder(order.table) },
                { label: 'Cancel' },
            ],
        });
    };

    confirmCloseSelected = (list) => {
        const { onCloseOrder } = this.props;
        const { selectedKeys } = this.state;
        const selected = list.filter(o => selectedKeys.includes(o.key));
        if (selected.length === 0) return;

        confirmAlert({
            title: `Close ${selected.length} order${selected.length !== 1 ? 's' : ''}?`,
            message: `${selected.length} order${selected.length !== 1 ? 's' : ''} will be closed. This can't be undone from here.`,
            buttons: [
                {
                    label: `Close ${selected.length}`,
                    onClick: () => {
                        selected.forEach(o => onCloseOrder && onCloseOrder(o.table));
                        this.setState(prev => ({
                            selectedKeys: prev.selectedKeys.filter(k => !selected.some(o => o.key === k)),
                        }));
                    },
                },
                { label: 'Cancel' },
            ],
        });
    };

    renderOrder = (order) => {
        const { onViewOrder } = this.props;
        const requestId = order.table && order.table.runningOrder && order.table.runningOrder.requestId;
        const checked = this.state.selectedKeys.includes(order.key);

        return (
            <div className="captain-progress-order" key={order.key}>
                <label className="captain-kot-item-check captain-progress-order-check">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => this.toggleSelected(order.key, e.target.checked)}
                    />
                </label>

                <div className="captain-progress-order-main">
                    <span className={`captain-progress-order-type captain-progress-order-type--${order.typeClass}`}>
                        {order.typeLabel}
                    </span>
                    <span className="captain-progress-order-label">{order.label}</span>
                    {order.orderNo && (
                        <span className="captain-progress-order-meta">{order.orderNo}</span>
                    )}
                </div>

                <div className="captain-progress-order-info">
                    {order.duration && (
                        <span className="captain-progress-order-duration">
                            <i className="pi pi-clock" /> {order.duration}
                        </span>
                    )}
                    {order.itemCount != null && (
                        <span className="captain-progress-order-items">
                            {order.itemCount} item{order.itemCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {order.amount != null && order.amount !== '' && (
                        <span className="captain-progress-order-amount">₹{order.amount}</span>
                    )}
                </div>

                <div className="captain-progress-order-actions">
                    <Button
                        className="p-button-text p-button-sm"
                        icon="pi pi-external-link"
                        label="View"
                        onClick={() => onViewOrder && onViewOrder(requestId)}
                        disabled={!requestId}
                    />
                    <Button
                        className="p-button-danger p-button-sm"
                        icon="pi pi-times-circle"
                        label="Close"
                        onClick={() => this.confirmCloseOrder(order)}
                    />
                </div>
            </div>
        );
    };

    renderList = (list) => {
        if (list.length === 0) {
            return <div className="captain-kot-empty">No orders here.</div>;
        }

        const { selectedKeys } = this.state;
        const selectedCount = list.filter(o => selectedKeys.includes(o.key)).length;
        const allSelected = selectedCount === list.length;

        return (
            <>
                <div className="captain-progress-select-all-row">
                    <label className="captain-kot-item-check">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => this.toggleSelectAll(list, e.target.checked)}
                        />
                        Select All
                    </label>

                    <span className="captain-progress-selected-count">{selectedCount} selected</span>

                    <Button
                        label="Close Selected"
                        icon="pi pi-times-circle"
                        className="p-button-danger p-button-sm"
                        disabled={selectedCount === 0}
                        onClick={() => this.confirmCloseSelected(list)}
                    />
                </div>

                <div className="captain-progress-order-list">
                    {list.map(this.renderOrder)}
                </div>
            </>
        );
    };

    tabHeader = (label, count) => (
        <span className="captain-resource-tab-header">
            {label}
            <span className="captain-resource-count">{count}</span>
        </span>
    );

    render() {
        const { visible, onHide, orders = [] } = this.props;
        const dineIn = orders.filter(o => o.typeClass === 'dinein');
        const takeaway = orders.filter(o => o.typeClass === 'takeaway');
        const delivery = orders.filter(o => o.typeClass === 'delivery');

        return (
            <Dialog
                header="Orders in Progress"
                visible={visible}
                onHide={onHide}
                className="captain-progress-modal"
                style={{ width: '620px' }}
                dismissableMask
            >
                {orders.length === 0 ? (
                    <div className="captain-kot-empty">No orders currently in progress.</div>
                ) : (
                    <TabView className="captain-resource-tabs">
                        <TabPanel header={this.tabHeader('All', orders.length)}>
                            {this.renderList(orders)}
                        </TabPanel>
                        <TabPanel header={this.tabHeader('Dine-in', dineIn.length)}>
                            {this.renderList(dineIn)}
                        </TabPanel>
                        <TabPanel header={this.tabHeader('Takeaway', takeaway.length)}>
                            {this.renderList(takeaway)}
                        </TabPanel>
                        <TabPanel header={this.tabHeader('Delivery', delivery.length)}>
                            {this.renderList(delivery)}
                        </TabPanel>
                    </TabView>
                )}
            </Dialog>
        );
    }
}

export default OrdersInProgressModal;
